import React from 'react';

import {GetStaticPaths, GetStaticProps} from 'next';

import {WebPage} from 'src/components/contentful';
import {LayoutProps} from 'src/components/layout/Layout';
import {StoreHero} from 'src/components/store-page/store-hero';
import {regionalConfigsArr} from 'src/config';
import PageNotFound from 'src/pages/404';
import {PageParams} from 'src/types';
import {contentfulApi} from 'src/utils/contentful/client';
import {IWebPage} from 'src/utils/contentful/content-models/generated/contentful';
import {
  indexSlugs,
  getImageAltFromAsset,
  getImageSourceFromAsset,
} from 'src/utils/contentful/content-models/util';

interface StorePageBaseProps extends LayoutProps {
  notFound: false;
  page: IWebPage;
}

type StorePageProps = StorePageBaseProps | {notFound: true};

export const getStaticPaths: GetStaticPaths<PageParams> = async () => {
  const paths: {params: PageParams}[] = [];

  for (const config of regionalConfigsArr) {
    const {locales, regionCode: region} = config;
    for (const locale of locales) {
      paths.push({params: {region, locale}});
    }
  }

  return {paths, fallback: false};
};

export const getStaticProps: GetStaticProps<
  StorePageProps,
  PageParams
> = async (ctx) => {
  const {params, preview} = ctx;

  if (!params) {
    throw new Error('Could not find page params');
  }

  const {region, locale} = params;

  const api = preview ? contentfulApi.preview : contentfulApi;
  const page = await api.getWebPage(indexSlugs.store, {locale});

  if (!page) {
    return {props: {notFound: true}};
  }

  const {title, description, image, includeInRegions} = page.fields;

  return {
    props: {
      notFound: false,
      page,
      pageContext: {
        region,
        locale,
      },
      seo: {
        title,
        description,
        includeInRegions,
        imageSrc: getImageSourceFromAsset(image),
        imageAltText: getImageAltFromAsset(image),
        slugPrefix: '',
        slug: indexSlugs.store,
        noIndex: false,
      },
      preview: !!preview,
    },
  };
};

const StorePage: React.FC<React.PropsWithChildren<StorePageProps>> = (
  props
) => {
  if (props.notFound) {
    return <PageNotFound />;
  }

  const {pageContext, seo, preview, page} = props;
  return (
    <WebPage
      pageEntry={page}
      pageContext={pageContext}
      seo={seo}
      preview={preview}
    >
      <StoreHero />
    </WebPage>
  );
};

export default StorePage;
